<script setup lang="ts">
import { Plus } from "@element-plus/icons-vue";
import type { OfferForm } from "@/shared/types";
import { MIN_OFFER_AMOUNT_USD } from "@/shared/consts";
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { data: user } = useAuth();
const { width } = useWindowSize();
const CurrencyStore = useCurrencyStore();
const { cryptoCurrencies, fiatCurrencies, availablePairs } =
  storeToRefs(CurrencyStore);
const PaymentOptionsStore = usePaymentOptionsStore();
const { baseOptionsGrouped } = storeToRefs(PaymentOptionsStore);

const isMobile = computed(() => width.value < 690);

const recieveSelected = defineModel<string | undefined>("recieveSelected", {
  required: true,
});
const sendSelected = defineModel<string | undefined>("sendSelected", {
  required: true,
});
const paymentMethod = defineModel<string[]>("paymentMethod", {
  required: true,
});
const amount = defineModel<string>("amount", {
  required: true,
});
const props = defineProps<{
  showAmount?: boolean;
  minTradeAmountSearched?: boolean;
  minAmountLimit?: number;
  useAvailablePairs?: boolean;
  offerForm?: OfferForm;
}>();

const showEditForm = ref<null | OfferForm>(null);

const offerFormWatcher = watch(
  () => props.offerForm,
  () => {
    if (props.offerForm) {
      showEditForm.value = props.offerForm;
    } else {
      showEditForm.value = null;
    }
  },
  { immediate: true }
);
onUnmounted(() => {
  offerFormWatcher();
});

const direction = computed(() =>
  route.params.action === "buy" ? "sellDirections" : "buyDirections"
);

const baseOptionsFiltred = computed(() => {
  const localGroup: any[] = [];
  if (!recieveSelected.value || recieveSelected.value === "") {
    return baseOptionsGrouped.value;
  }
  baseOptionsGrouped.value.forEach((group) => {
    const localChildren: any[] = [];
    group.children.forEach((child) => {
      if (child.currency === recieveSelected.value) {
        localChildren.push(child);
      }
    });
    if (localChildren.length) {
      localGroup.push({ ...group, children: localChildren });
    }
  });
  return localGroup;
});
const addOptionModel = ref(false);
const filterCurrency = ref();
const freshCreatedOption = ref<number | undefined>();

const showTFAPopup = ref(false);
const onAddNewOffer = () => {
  if (!user.value) {
    ElMessage.warning(t("p-trade-needLogin"));
    return;
  }
  if (!user.value?.tfaEnabled) {
    showTFAPopup.value = true;
    return;
  }
  showEditForm.value = {
    type: "",
    currencyIn: sendSelected.value,
    currencyOut: recieveSelected.value,
  };
};
const localePath = useLocalePath();
const emit = defineEmits(["fetchUserOffers"]);
const onCloseOfferForm = (opts?: any) => {
  showEditForm.value = null;
  if (opts?.fetch) {
    emit("fetchUserOffers");
    router.push(localePath("/trade/my-offers"));
  }
};
const onAddOption = (option?: string) => {
  filterCurrency.value = option;
  addOptionModel.value = true;
};
const onOptionCreated = (newOptionId?: number) => {
  freshCreatedOption.value = newOptionId;
  addOptionModel.value = false;
};
</script>

<template>
  <el-row class="filters mb-2" :gutter="10" align="top">
    <el-col :span="-1" class="currency-col">
      <span v-if="!useAvailablePairs" class="switch-col mr-2"> crypto: </span>
      <span class="switch-col mr-2" v-else-if="route.params.action === 'buy'">
        {{ t("p-trade-buying") }}
      </span>
      <span class="switch-col mr-2" v-else>
        {{ t("p-trade-selling") }}
      </span>
      <el-select
        v-model="sendSelected"
        :size="isMobile ? 'default' : 'large'"
        :style="isMobile ? 'width: 100%' : 'width: 130px'"
        filterable
        :placeholder="t('p-trade-placeholder-type')"
        @change="paymentMethod = []"
        :clearable="!useAvailablePairs"
      >
        <el-option-group
          v-for="(group, key) in { crypto: cryptoCurrencies }"
          :key="key"
          :label="key"
        >
          <el-option
            v-for="item in group"
            :key="item.name"
            :label="item.name"
            :value="item.name"
            :class="
              useAvailablePairs &&
              !availablePairs?.[direction]?.[item.name]?.includes(
                recieveSelected || ''
              )
                ? 'opacity-35'
                : ''
            "
          />
        </el-option-group>
      </el-select>
    </el-col>
    <el-col :span="-1" class="currency-col">
      <span v-if="!useAvailablePairs" class="switch-col mr-2"> fiat: </span>
      <span v-else class="switch-col mr-2">{{ t("p-trade-for") }}</span>
      <el-select
        :size="isMobile ? 'default' : 'large'"
        :style="isMobile ? 'width: 100%' : 'width: 130px'"
        v-model="recieveSelected"
        filterable
        :placeholder="t('p-trade-placeholder-type')"
        @change="paymentMethod = []"
        :clearable="!useAvailablePairs"
      >
        <el-option-group
          v-for="(group, key) in { fiat: fiatCurrencies }"
          :key="key"
          :label="key"
        >
          <el-option
            v-for="item in group"
            :key="item.name"
            :label="item.name"
            :value="item.name"
            :class="
              useAvailablePairs &&
              !availablePairs?.[direction]?.[sendSelected || '']?.includes(
                item.name
              )
                ? 'opacity-35'
                : ''
            "
          />
        </el-option-group>
      </el-select>
    </el-col>
    <el-col :span="-1" class="payment-col">
      &nbsp;
      <el-select
        v-model="paymentMethod"
        :placeholder="t('p-trade-placeholder-allOptions')"
        filterable
        :size="isMobile ? 'default' : 'large'"
        :style="isMobile ? 'width: 100%' : 'width: 230px'"
        collapse-tags-tooltip
        collapse-tags
        multiple
        clearable
      >
        <el-option-group
          v-for="group in baseOptionsFiltred"
          :key="group.bpo_id"
          :label="group.name"
        >
          <el-option
            v-for="item in group.children"
            :key="item.bpo_id"
            :label="`${item.name} - ${item.currency}`"
            :value="item.bpo_id.toString()"
          />
        </el-option-group>
      </el-select>
    </el-col>
    <el-col :span="-1" class="payment-col" v-if="showAmount">
      {{ t("p-trade-amountIn") }} {{ recieveSelected }}:
      <el-input
        clearable
        type="number"
        v-model="amount"
        :size="isMobile ? 'default' : 'large'"
        :style="isMobile ? 'width: 100%' : 'width: 130px'"
        :placeholder="t('p-trade-placeholder-anyAmount')"
      />
      <br />
      <Transition>
        <div v-if="minTradeAmountSearched" class="mt-1">
          <el-alert type="warning" :closable="false">
            {{ t("p-trade-minimumLimit") }}
            {{ minAmountLimit }}
            {{ recieveSelected }} ({{
              t("p-trade-equivalent", {
                MIN_OFFER_AMOUNT_USD,
              })
            }})
          </el-alert>
        </div>
      </Transition>
    </el-col>
    <div class="flex-grow"></div>
    <el-col :span="-1" class="control-col">
      <el-button
        round
        :size="isMobile ? 'default' : 'large'"
        class="new-offer-btn"
        :icon="Plus"
        type="warning"
        @click="onAddNewOffer"
        plain
      >
        {{ t("p-trade-postNewOffer") }}
      </el-button>
      <OffersEditForm
        @addOption="onAddOption"
        @close="onCloseOfferForm"
        :pre-created-option="freshCreatedOption"
        v-if="showEditForm"
        :form="showEditForm"
      />
      <PaymentOptionCreate
        v-if="addOptionModel"
        @close="onOptionCreated"
        :filterCurrency="filterCurrency"
      />
    </el-col>
    <UserTFAAlert v-model:dialogVisible="showTFAPopup" />
  </el-row>
</template>

<style scoped lang="scss">
.filters {
  :deep(.el-col) {
    margin-bottom: 5px;
  }
  @media (max-width: 690px) {
    display: block;

    &::after {
      content: "";
      display: block;
      clear: both;
      float: none;
    }

    .currency-col {
      width: 50%;
      float: left;
    }
    .switch-col {
      width: 10%;
      float: left;
      text-align: center;
      margin-top: 10px;
    }
    .payment-col {
      width: 50%;
      float: left;
    }
    .filters-col {
      width: 30%;
      float: left;
      .el-button {
        width: 100%;
      }
    }
    .flex-grow {
      display: none;
    }
  }
  @media (max-width: 470px) {
    .ALM-col {
      float: left;
    }
  }
}
</style>
